import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import SettingSwitch from '@/components/SettingSwitch';
import { config } from '@/config';
import { Ga4Event } from '@/constants/enum';
import { checkShowErrorInline, handleToastMutation } from '@/helpers';
import { useGa4 } from '@/hooks/useGa4';
import i18n, { supportLanguages } from '@/i18n';
import { apiCaller } from '@/redux/query';
import slice from '@/redux/slice';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { dataSettingsSelector, loadingSelector } from '@/redux/slice/dataSettings.slice';
import { gaFourSelector } from '@/redux/slice/session.slice';
import toastSlice from '@/redux/slice/toast.slice';
import { BlockStack, Button, Card, InlineStack, Link, Modal, Select, Tooltip } from '@shopify/polaris';
import { DuplicateIcon, HideIcon, ResetIcon, ViewIcon } from '@shopify/polaris-icons';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { GeneralSettingsStyled } from './styled';

function GeneralSettings() {
  const { t } = useTranslation(['common', 'settings']);
  const gaFour = useSelector(gaFourSelector);
  const { handleGa4 } = useGa4();
  const dispatch = useDispatch();
  const isLoading = useSelector(loadingSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const adminURL = apiCaller.useGetAdminURLQuery(config.shop, { skip: isSkip });
  const [enableApp, enableStatus] = apiCaller.useEnableAppMutation();
  const [refreshAdminUrl, adminUrlStatus] = apiCaller.useRefreshAdminUrlMutation();
  // const [emailFraudOrder, { isLoading: isUpdatingEmail }] = apiCaller.useEmailFraudOrderMutation();
  const [adminAccessLink, setAdminAccessLink] = useState<string | undefined>(adminURL?.data?.url);
  // const [email, setEmail] = useState<string | null>(null);
  // const [emailError, setEmailError] = useState<string>('');
  const [state, setState] = useState({
    modalOpen: false,
    copied: false,
    showFullUrl: false,
  });

  // useEffect(() => {
  //   if (dataSettings && dataSettings?.settings.user.fraudOrderEmail !== undefined) {
  //     setEmail(
  //       dataSettings?.settings.user.fraudOrderEmail === ''
  //         ? dataSettings?.settings.user.email ?? ''
  //         : dataSettings?.settings.user.fraudOrderEmail ?? '',
  //     );
  //   }
  // }, [dataSettings]);

  const handleCopyUrl = useCallback(() => {
    const textToCopy = adminAccessLink || '';

    try {
      // Thử Clipboard API trước
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          setState({ ...state, copied: true });
          dispatch(
            slice.toastSlice.actions.handleToast({
              isOpen: true,
              content: t('common:copied'),
              error: false,
            }),
          );
        })
        .catch(() => {
          // Nếu Clipboard API bị chặn → fallback execCommand
          fallbackCopyText(textToCopy);
        });
    } catch {
      fallbackCopyText(textToCopy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminAccessLink, dispatch, state, t]);

  const fallbackCopyText = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      const successful = document.execCommand('copy');
      console.log(successful ? 'Fallback copied!' : 'Fallback failed');
    } catch (err) {
      console.error('Fallback copy failed', err);
    }

    document.body.removeChild(textarea);

    // Bạn cũng có thể dispatch toast ở đây nếu muốn:
    dispatch(
      slice.toastSlice.actions.handleToast({
        isOpen: true,
        content: text ? 'Copied!' : 'Nothing to copy',
        error: false,
      }),
    );
  };
  // const handleEmailChange = useCallback((value: string) => {
  //   setEmail(value);
  //   setEmailError('');
  // }, []);

  // const handleEmailBlur = useCallback(() => {
  //   if (!email) {
  //     setEmailError(t('common:email_required'));
  //   } else if (!validateEmail(email)) {
  //     setEmailError(t('common:invalid_email_address'));
  //   } else {
  //     emailFraudOrder({
  //       sendFraudOrder: dataSettings?.settings.user.sendFraudOrder,
  //       fraudOrderEmail: email ?? '',
  //     }).then((res) => {
  //       const condition = checkShowErrorInline(res);
  //       if (!condition.status) {
  //         dispatch(toastSlice.actions.handleToast(handleToastMutation(res, t)));
  //         setEmailError('');
  //       }
  //     });
  //   }
  // }, [email, dataSettings?.settings.user.sendFraudOrder, emailFraudOrder, dispatch, t]);

  const handleShowUrl = useCallback(() => {
    setState({ ...state, showFullUrl: true });
  }, [state]);

  const handleHideUrl = useCallback(() => {
    setState({ ...state, showFullUrl: false });
  }, [state]);

  const handleOpenModalEnableApp = useCallback(() => {
    if (dataSettings?.settings.user.enableApp) {
      setState({ ...state, modalOpen: true });
    } else {
      enableApp(!dataSettings?.settings.user.enableApp).then((res) => {
        const condition = checkShowErrorInline(res);
        if (!condition.status) {
          dispatch(toastSlice.actions.handleToast(handleToastMutation(res, t)));
        }
      });
    }
  }, [dataSettings?.settings.user.enableApp, dispatch, enableApp, state, t]);

  const handleConfirmToggle = useCallback(() => {
    enableApp(!dataSettings?.settings.user.enableApp).then((res) => {
      const condition = checkShowErrorInline(res);
      if (!condition.status) {
        dispatch(toastSlice.actions.handleToast(handleToastMutation(res, t)));
      }
    });
    setState({ ...state, modalOpen: false });
  }, [dataSettings?.settings.user.enableApp, dispatch, enableApp, state, t]);

  const handleCancelToggle = useCallback(() => {
    setState({ ...state, modalOpen: false });
  }, [state]);

  const handleOpenAdminAccess = useCallback(() => {
    window.open(adminAccessLink);
  }, [adminAccessLink]);

  const handleChangeLanguage = useCallback(
    (value: string) => {
      i18n.changeLanguage(value).then(() => {
        dispatch(
          toastSlice.actions.handleToast({
            content: t('text_updated'),
            error: false,
            isOpen: true,
          }),
        );
      });
    },
    [dispatch, t],
  );

  // const handleToggleNotifications = useCallback(() => {
  //   const emailToSend = dataSettings?.settings.user.sendFraudOrder
  //     ? dataSettings?.settings.user.fraudOrderEmail === ''
  //       ? dataSettings?.settings.user.email ?? ''
  //       : dataSettings?.settings.user.fraudOrderEmail ?? ''
  //     : email ?? '';

  //   emailFraudOrder({
  //     sendFraudOrder: !dataSettings?.settings.user.sendFraudOrder,
  //     fraudOrderEmail: emailToSend,
  //   }).then((res) => {
  //     const condition = checkShowErrorInline(res);
  //     if (!condition.status) {
  //       dispatch(toastSlice.actions.handleToast(handleToastMutation(res, t)));
  //       setEmail(emailToSend);
  //       setEmailError('');
  //     }
  //   });
  // }, [
  //   dataSettings?.settings.user.sendFraudOrder,
  //   dataSettings?.settings.user.fraudOrderEmail,
  //   dataSettings?.settings.user.email,
  //   email,
  //   emailFraudOrder,
  //   dispatch,
  //   t,
  // ]);

  if (!gaFour.generalSetting) {
    handleGa4(Ga4Event.GeneralSetting);
  }

  const handleRefreshAdminUrl = useCallback(() => {
    refreshAdminUrl().then((res) => {
      if ('data' in res) {
        setAdminAccessLink(res.data.url);
      }
      const condition = checkShowErrorInline(res);
      if (!condition.status) {
        dispatch(toastSlice.actions.handleToast(handleToastMutation(res, t)));
      }
    });
  }, [dispatch, refreshAdminUrl, t]);

  useEffect(() => {
    if (adminURL.data?.url) {
      setAdminAccessLink(adminURL.data.url);
    }
  }, [adminURL]);

  return (
    <GeneralSettingsStyled>
      <BlockStack gap="400" inlineAlign="stretch">
        <SettingSwitch
          trackAction="setting_activate_app_blockify"
          title={t('settings:activate_blockify')}
          settingToggleProps={{
            enabled: dataSettings?.settings.user.enableApp,
            action: {
              loading: enableStatus.isLoading || isLoading,
              onAction: handleOpenModalEnableApp,
            },
          }}
        />
        <Card>
          <InlineStack align="space-between" blockAlign="center">
            <BoldText>{t('settings:select_language')}</BoldText>
            <Select options={supportLanguages} value={i18n.language} label="" labelHidden onChange={handleChangeLanguage} />
          </InlineStack>
        </Card>

        {/* <Card>
          <BoldText>{t('settings:notifications_key')}</BoldText>
          <BlockStack gap="100">
            <Checkbox
              label={t('settings:daily_fraud_order_emails')}
              checked={dataSettings?.settings.user.sendFraudOrder}
              onChange={handleToggleNotifications}
            />
            <Collapsible id="basic-collapsible" open={dataSettings?.settings.user.sendFraudOrder || false} transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}>
              {dataSettings ? (
                <TextField
                  label={t('settings:email_address')}
                  value={email ?? ''}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  disabled={isUpdatingEmail}
                  placeholder={email ?? ''}
                  autoComplete="off"
                  error={emailError}
                  requiredIndicator
                />
              ) : (
                <SkeletonBodyText />
              )}
            </Collapsible>
          </BlockStack>
        </Card> */}

        <Card>
          <BoldText>{t('settings:admins_access_url')}</BoldText>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '8px 0px' }}>
            <div style={{ maxWidth: '80%', wordBreak: 'break-word' }}>
              <Link external removeUnderline onClick={handleOpenAdminAccess}>
                {state.showFullUrl ? adminAccessLink : `${adminAccessLink?.substring(0, 20)}***************` || ''}
              </Link>
            </div>
            <div className="d-flex">
              <Tooltip content={!state.showFullUrl ? t('common:show') : t('common:hide')}>
                <Button
                  icon={state.showFullUrl ? ViewIcon : HideIcon}
                  onClick={state.showFullUrl ? handleHideUrl : handleShowUrl}
                />
              </Tooltip>
              <div className="ml-8">
                <Tooltip content={t('common:copy')}>
                  <Button icon={DuplicateIcon} onClick={handleCopyUrl} />
                </Tooltip>
              </div>
              <div className="ml-8">
                <Tooltip content={t('common:refresh')}>
                  <Button icon={ResetIcon} onClick={handleRefreshAdminUrl} loading={adminUrlStatus.isLoading} />
                </Tooltip>
              </div>
            </div>
          </div>
          <RegularText>{t('settings:access_url_not_blocked')}</RegularText>
        </Card>
      </BlockStack>

      <Modal
        open={state.modalOpen}
        onClose={handleCancelToggle}
        title={t('settings:turn_off_blockify')}
        primaryAction={{
          content: t('common:turn_off'),
          onAction: handleConfirmToggle,
          destructive: true,
        }}
        secondaryActions={[
          {
            content: t('common:cancel'),
            onAction: handleCancelToggle,
          },
        ]}
      >
        <Modal.Section>
          <RegularText>{t('settings:blockify_protection_disabled')}</RegularText>
        </Modal.Section>
      </Modal>
    </GeneralSettingsStyled>
  );
}

export default memo(GeneralSettings);
